import { supabase } from './supabase';
import { AppState, AppStateStatus } from 'react-native';
import { timeoutLogger, TimeoutErrorCode, TimeoutSeverity } from '@/utils/timeoutLogger';
import { createTracer } from '@/utils/requestTracer';
import { isCountrySupported, DEFAULT_FALLBACK_COUNTRY } from '@/config/supportedCountries';

/**
 * Country Detection Service
 *
 * STRATEGY:
 * - All platforms (Web, iOS, Android): Uses GeoIP edge function as the sole source of truth
 * - GeoIP determines the user's real country based on their IP address (4s timeout)
 * - Device locale is NOT used for country detection
 *
 * CACHING:
 * - Session-based in-memory cache (cleared on background and logout)
 * - No persistent storage to ensure fresh detection on each active session
 *
 * VALIDATION (two-level):
 * - Level 1 (primary): Dynamic validation via campaign_countries_languages DB table (2s timeout)
 * - Level 2 (fallback): Static list in config/supportedCountries.ts when DB is unreachable
 * - Falls back to US only when country has no configurations at either level
 * - US is guaranteed to have at least one campaign configuration
 */

const VALIDATION_TIMEOUT_MS = 2000;

// ============================================================================
// SESSION-BASED CACHE
// ============================================================================

interface SessionCache {
  countryCode: string;
  detectionMethod: 'geoip' | 'fallback';
}

let sessionCache: SessionCache | null = null;

// In-memory cache for country validation (persists during app session)
const countryValidationCache = new Map<string, boolean>();

/**
 * Clears the session cache
 * Called when:
 * - App goes to background
 * - User logs out
 */
export function clearCountryCache(): void {
  console.log('🌍 [CountryDetection] 🗑️ Clearing session cache');
  sessionCache = null;
}

/**
 * Returns cached country if available
 */
function getCachedCountry(): string | null {
  if (sessionCache) {
    //console.log(`🌍 [CountryDetection] ✅ Using cached country: ${sessionCache.countryCode} (${sessionCache.detectionMethod})`);
    return sessionCache.countryCode;
  }
  return null;
}

/**
 * Caches country for current session
 */
function cacheCountry(countryCode: string, method: 'geoip' | 'fallback'): void {
  sessionCache = { countryCode, detectionMethod: method };
  //console.log(`🌍 [CountryDetection] 💾 Cached country: ${countryCode} (${method})`);
}

// ============================================================================
// APP STATE MONITORING FOR CACHE CLEARING
// ============================================================================

let appStateSubscription: any = null;
let isMonitoring = false;

/**
 * Starts monitoring app state to clear cache on background
 */
export function startAppStateMonitoring(): void {
  if (isMonitoring) {
    //console.log('🌍 [CountryDetection] ⚠️ AppState monitoring already active');
    return;
  }

  //console.log('🌍 [CountryDetection] 👀 Starting AppState monitoring');

  const handleAppStateChange = (nextAppState: AppStateStatus) => {
    //console.log('🌍 [CountryDetection] AppState changed to:', nextAppState);

    // Clear cache when app goes to background or inactive
    if (nextAppState === 'background' || nextAppState === 'inactive') {
      //console.log('🌍 [CountryDetection] App backgrounded, clearing cache');
      clearCountryCache();
    }
  };

  appStateSubscription = AppState.addEventListener('change', handleAppStateChange);
  isMonitoring = true;
}

/**
 * Stops monitoring app state (cleanup)
 */
export function stopAppStateMonitoring(): void {
  if (appStateSubscription) {
    //console.log('🌍 [CountryDetection] 🛑 Stopping AppState monitoring');
    appStateSubscription.remove();
    appStateSubscription = null;
    isMonitoring = false;
  }
}

// ============================================================================
// COUNTRY VALIDATION
// ============================================================================

/**
 * Validates if a country code has at least one campaign configuration.
 *
 * Two-level validation:
 * - Level 1: Queries campaign_countries_languages in DB (protected by 2s timeout)
 * - Level 2: Falls back to static isCountrySupported() if DB is unreachable
 *
 * Uses in-memory cache to avoid repeated database queries within the same session.
 */
async function validateCountryHasCampaign(countryCode: string): Promise<boolean> {
  const countryCodeLower = countryCode.toLowerCase();

  if (countryValidationCache.has(countryCodeLower)) {
    return countryValidationCache.get(countryCodeLower)!;
  }

  try {
    const dbQuery = supabase
      .from('campaign_countries_languages')
      .select('id')
      .eq('country_code', countryCodeLower)
      .limit(1)
      .maybeSingle();

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`Validation timeout after ${VALIDATION_TIMEOUT_MS}ms`)), VALIDATION_TIMEOUT_MS)
    );

    const { data, error } = await Promise.race([dbQuery, timeoutPromise]);

    if (error) {
      console.error(`🌍 [CountryDetection] ❌ Error validating country "${countryCodeLower}":`, error);
      const fallback = isCountrySupported(countryCode);
      console.warn(`🌍 [CountryDetection] Using static fallback for "${countryCodeLower}": ${fallback}`);
      return fallback;
    }

    const hasConfig = data !== null;
    countryValidationCache.set(countryCodeLower, hasConfig);
    return hasConfig;
  } catch (error) {
    console.warn(`🌍 [CountryDetection] ⚠️ DB validation failed for "${countryCodeLower}", falling back to static list`);
    const fallback = isCountrySupported(countryCode);
    return fallback;
  }
}

// ============================================================================
// GEOIP COUNTRY DETECTION (ALL PLATFORMS)
// ============================================================================

export type GeoIpSuccess = {
  code: 200;
  error: 0;
  data: { success: string }
};

export type GeoIpError = {
  code: string;
  error: 1;
  messages: string
};

/**
 * Gets country from GeoIP edge function (all platforms)
 * Lightweight with 4s timeout for fast fallback
 * @returns Country code from GeoIP API
 */
async function getCountryFromGeoIP(): Promise<string> {
  const timestamp = new Date().toISOString();
  const tracer = createTracer('GeoIP Country Detection', 4000);
  const startTime = Date.now();

  try {
    const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
    if (!supabaseUrl) {
      tracer.complete({ result: 'error', error: 'EXPO_PUBLIC_SUPABASE_URL not configured' });
      throw new Error('EXPO_PUBLIC_SUPABASE_URL not configured');
    }

    const apiUrl = `${supabaseUrl}/functions/v1/geoip`;
    tracer.addCheckpoint('Starting GeoIP request', { url: apiUrl });

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const response = await Promise.race([
      fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
        },
      }),
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          const actualDuration = Date.now() - startTime;
          tracer.timeout(actualDuration);

          timeoutLogger.logTimeout({
            requestId: tracer.getRequestId(),
            operationName: 'GeoIP Country Detection',
            timeoutDuration: 4000,
            actualDuration,
            errorCode: TimeoutErrorCode.GEOIP_REQUEST,
            severity: TimeoutSeverity.MEDIUM,
            metadata: { url: apiUrl },
          }).catch(err => console.error('Failed to log GeoIP timeout:', err));

          reject(new Error('Request timeout after 4 seconds'));
        }, 4000);
      })
    ]);

    clearTimeout(timeoutId);

    tracer.addCheckpoint('GeoIP response received', { status: response.status });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unable to read error body');
      tracer.complete({ result: 'error', status: response.status, error: errorText });
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    tracer.addCheckpoint('Parsing GeoIP response');
    const json = await response.json() as GeoIpSuccess | GeoIpError;

    if (json.error === 0) {
      const countryCode = json.data.success;
      tracer.complete({ result: 'success', countryCode });
      return countryCode;
    } else {
      tracer.complete({ result: 'error', errorCode: json.code, errorMessage: json.messages });
      throw new Error(`${json.code}: ${json.messages}`);
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error(`🌍 [${timestamp}] [${tracer.getRequestId()}] CountryDetection: ❌ GeoIP failed:`, errorMessage);
    throw error;
  }
}

// ============================================================================
// MAIN DETECTION FUNCTION
// ============================================================================

/**
 * Detects user's country using GeoIP as the sole source of truth
 *
 * Flow:
 * 1. Check session cache (instant return if available)
 * 2. Call GeoIP edge function (4s timeout) on all platforms
 * 3. Validate country via validateCountryHasCampaign() -- DB query with 2s timeout,
 *    falls back to static list if DB is unreachable
 * 4. If no valid country found, fallback to US
 *
 * @returns Promise<string> - Country code (always returns a valid country)
 */
export async function detectCountry(): Promise<string> {
  const cached = getCachedCountry();
  if (cached) {
    return cached;
  }

  let detectedCountry: string | null = null;
  let detectionMethod: 'geoip' | 'fallback' = 'fallback';

  try {
    try {
      detectedCountry = await getCountryFromGeoIP();
      detectionMethod = 'geoip';
    } catch (geoipError) {
      console.warn('🌍 [CountryDetection] ⚠️ GeoIP failed, using fallback');
    }

    if (detectedCountry && await validateCountryHasCampaign(detectedCountry)) {
      cacheCountry(detectedCountry, detectionMethod);
      return detectedCountry;
    } else if (detectedCountry) {
      console.warn(`🌍 [CountryDetection] ⚠️ Country "${detectedCountry}" not validated, using fallback`);
    }

    cacheCountry(DEFAULT_FALLBACK_COUNTRY, 'fallback');
    return DEFAULT_FALLBACK_COUNTRY;

  } catch (error) {
    console.error('🌍 [CountryDetection] ❌ UNEXPECTED ERROR:', error);
    cacheCountry(DEFAULT_FALLBACK_COUNTRY, 'fallback');
    return DEFAULT_FALLBACK_COUNTRY;
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Gets friendly country name from country code
 */
export function getCountryName(countryCode: string): string {
  const countryNames: Record<string, string> = {
    'US': 'United States',
    'FR': 'France',
    'GB': 'United Kingdom',
    'DE': 'Germany',
    'ES': 'Spain',
    'IT': 'Italy',
    'CA': 'Canada',
    'AU': 'Australia',
    'JP': 'Japan',
    'BR': 'Brazil',
    'MX': 'Mexico',
    'IN': 'India',
    'CN': 'China',
    'RU': 'Russia',
  };

  return countryNames[countryCode] || countryCode;
}

// Legacy export for backward compatibility
// Will be deprecated once all imports are updated
export async function getCountryByIp(): Promise<string> {
  console.warn('🌍 [CountryDetection] ⚠️ getCountryByIp() is deprecated, use detectCountry() instead');
  return detectCountry();
}
